import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { Logger } from "pino";
import { apiUtilLogger } from "../logger/logger";
import { ApiResponse } from "@/lib/api/apiTypes";

// Define types for the headers
type SecurityHeaders = {
  "Content-Security-Policy": string;
  "X-Frame-Options": string;
  "X-Content-Type-Options": string;
  "Referrer-Policy": string;
  "Permissions-Policy": string;
  "Strict-Transport-Security"?: string; // Optional because it's only set in production
};

type AuthHeaders = {
  "X-AUTH-TOKEN": string | undefined;
  "X-AUTH-PRINCIPAL": string | undefined;
};

const AUTH_USERNAME = "X-AUTH-USERNAME";

// Define the headers using the types
const AUTH_HEADERS: AuthHeaders = {
  "X-AUTH-TOKEN": process.env.API_APP_TOKEN,
  "X-AUTH-PRINCIPAL": process.env.API_APP_USER,
};

const SECURITY_HEADERS: SecurityHeaders = {
  "Content-Security-Policy": (() => {
    const base = "default-src 'self'; script-src 'self' 'unsafe-inline'; object-src 'none';";
    if (process.env.NODE_ENV !== 'production') {
      // Allow Next dev server (source-map/HMR) connections during development
      return base + " connect-src 'self' http://localhost:3000 ws://localhost:3000;";
    }
    return base;
  })(),
  "X-Frame-Options": "SAMEORIGIN",
  "X-Content-Type-Options": "nosniff",
  "Referrer-Policy": "no-referrer-when-downgrade",
  "Permissions-Policy": "geolocation=(self), microphone=()",
};

if (
  process.env.NODE_ENV === "production" &&
  process.env.APP_ENV !== "dev" &&
  process.env.APP_DOMAIN !== "localhost"
) {
  SECURITY_HEADERS["Strict-Transport-Security"] =
    "max-age=31536000; includeSubDomains";
} else if (
  process.env.APP_ENV === "dev" &&
  process.env.APP_DOMAIN !== "localhost"
) {
  SECURITY_HEADERS["Strict-Transport-Security"] = "max-age=20";
}

const DEFAULT_HEADERS = { ...AUTH_HEADERS, ...SECURITY_HEADERS };

/**
 * Generates headers by merging default headers with optional override headers.
 * @param overrideHeaders - Optional headers to override the default headers.
 * @returns The generated headers.
 */
export const generateHeaders = (
  overrideHeaders: Record<string, string> | null = null,
) => {
  return overrideHeaders
    ? { ...DEFAULT_HEADERS, ...overrideHeaders }
    : { ...DEFAULT_HEADERS };
};

/**
 * Generates authentication headers by adding the username to the default headers.
 * @param username - The username for authentication.
 * @returns The generated authentication headers.
 */
export const generateAuthHeaders = (username: string) => {
  return {
    ...DEFAULT_HEADERS,
    [AUTH_USERNAME]: username,
  };
};

/**
 * Creates a debug handler that logs the response.
 * @param logger - The logger instance.
 * @param endpoint - The API endpoint.
 * @returns A function that logs the response and returns it.
 */
export const createDebugHandler =
  (logger: Logger, endpoint: string) => (response: AxiosResponse) => {
    logger.debug(`Response returned for ${endpoint}`, response);
    return response;
  };

/**
 * Creates an error handler that logs the error and rejects the promise.
 * @param logger - The logger instance.
 * @param endpoint - The API endpoint.
 * @returns A function that logs the error and rejects the promise.
 */
export const createErrorHandler =
  (logger: Logger, endpoint: string) => (error: AxiosError) => {
    logger.error(`API Error with ${endpoint}`, error);
    // Re-throw the original AxiosError so callers can inspect error.response?.status
    return Promise.reject(error);
  };

export const apiClient = axios.create({
  baseURL: process.env.API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

apiClient.interceptors.response.use(
  (response) => {
    const url = response.config.url ?? "Unknown URL";
    return createDebugHandler(apiUtilLogger, url)(response);
  },
  (error) => {
    const url = error.config?.url || "Unknown URL";
    return createErrorHandler(apiUtilLogger, url)(error);
  },
);

/**
 * Makes an API call with the provided configuration and logs the response or error.
 * @param config - The Axios request configuration.
 * @param logger - The logger instance.
 * @param endpoint - The API endpoint.
 * @param overrideHeaders - Optional headers to override the default headers.
 * @returns A promise that resolves to an AxiosResponse.
 */
export const apiCall = <T>(
  config: AxiosRequestConfig,
  logger: Logger,
  endpoint: string,
  overrideHeaders: Record<string, string> | null = null,
): ApiResponse<AxiosResponse<T>> => {
  const headers = generateHeaders(overrideHeaders);

  return axios({ ...config, headers })
    .then(createDebugHandler(logger, endpoint))
    .catch(createErrorHandler(logger, endpoint));
};

/**
 * Makes an API call with the provided configuration, adds totalItems to the response data, and logs the response or error.
 * @param config - The Axios request configuration.
 * @param logger - The logger instance.
 * @param endpoint - The API endpoint.
 * @param overrideHeaders - Optional headers to override the default headers.
 * @returns A promise that resolves to an AxiosResponse.
 */
export const apiDataCall = <T>(
  config: AxiosRequestConfig,
  logger: Logger,
  endpoint: string,
  overrideHeaders: Record<string, string> | null = null,
): ApiResponse<AxiosResponse<T>> => {
  const headers = generateHeaders(overrideHeaders);

  return axios({ ...config, headers })
    .then((response) => {
      response.data.totalItems = response.headers['x-total-count'];
      return createDebugHandler(logger, endpoint)(response);
    })
    .catch(createErrorHandler(logger, endpoint));
};

/**
 * A higher-order function that applies a transformation to the result of an API call.
 * @param apiCall - The API call function to wrap.
 * @param dataTransformer - The function to transform the data.
 * @returns A new function that performs the API call and applies the data transformation.
 */
export const withDataTransformer = <T, R>(
  apiCall: (
    config: AxiosRequestConfig,
    logger: Logger,
    endpoint: string,
    overrideHeaders?: Record<string, string> | null,
  ) => Promise<AxiosResponse<T>>,
  dataTransformer: (data: T) => R,
) => {
  return async (
    config: AxiosRequestConfig,
    logger: Logger,
    endpoint: string,
    overrideHeaders?: Record<string, string> | null,
  ): Promise<AxiosResponse<R>> => {
    const response = await apiCall(config, logger, endpoint, overrideHeaders);
    return {
      ...response,
      data: dataTransformer(response.data),
      totalItems: response.headers['x-total-count'] ? Number(response.headers['x-total-count']) : 0,
    };
  };
};

export default apiClient;
